import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { raffleService } from '../services/raffleService';
import { CreateRaffleData } from '../types';
import toast from 'react-hot-toast';

export const usePublicRaffles = (params?: { page?: number; limit?: number; countryCode?: string }) => {
  return useQuery({
    queryKey: ['raffles', 'public', params],
    queryFn: () => raffleService.getPublicRaffles(params),
  });
};

export const useMyRaffles = (params?: { page?: number; limit?: number; status?: string }) => {
  return useQuery({
    queryKey: ['raffles', 'my', params],
    queryFn: () => raffleService.getMyRaffles(params),
  });
};

export const useRaffle = (id: string) => {
  return useQuery({
    queryKey: ['raffle', id],
    queryFn: () => raffleService.getRaffleById(id),
    enabled: !!id,
  });
};

export const useCreateRaffle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateRaffleData) => raffleService.createRaffle(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raffles', 'my'] });
      toast.success('Raffle created successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create raffle');
    },
  });
};

export const useUpdateRaffle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateRaffleData> }) =>
      raffleService.updateRaffle(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['raffle', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['raffles', 'my'] });
      toast.success('Raffle updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update raffle');
    },
  });
};

export const useDeleteRaffle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => raffleService.deleteRaffle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raffles', 'my'] });
      toast.success('Raffle deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete raffle');
    },
  });
};

export const useCloseRaffle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => raffleService.closeRaffle(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['raffle', id] });
      queryClient.invalidateQueries({ queryKey: ['raffles', 'my'] });
      toast.success('Raffle closed successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to close raffle');
    },
  });
};

export const usePauseRaffle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => raffleService.pauseRaffle(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['raffle', id] });
      queryClient.invalidateQueries({ queryKey: ['raffles', 'my'] });
      toast.success('Raffle paused successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to pause raffle');
    },
  });
};

export const useRaffleDraws = (
  raffleId: string,
  params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }
) => {
  return useQuery({
    queryKey: ['raffle', raffleId, 'draws', params],
    queryFn: () => raffleService.getRaffleDraws(raffleId, params),
    enabled: !!raffleId,
  });
};

export const usePublicRaffleDraws = (
  raffleId: string,
  params?: {
    page?: number;
    limit?: number;
    drawDate?: string;
  }
) => {
  return useQuery({
    queryKey: ['raffle', raffleId, 'draws', 'public', params],
    queryFn: () => raffleService.getPublicRaffleDraws(raffleId, params),
    enabled: !!raffleId,
  });
};
